/* eslint-disable prettier/prettier */
import { useState, useEffect, useRef } from "react";
import { color } from "@styles";
import { P1 } from "components/atoms/Paragraph";
import { Asterisk, FormContainer, Grid2 } from "./styles";
import { QuoteSubmit } from "./QuoteSubmit";
import { getCookie, HubspotForms, isAllowedEmail, parseQuery } from "@utils";
import Bugsnag from "@bugsnag/js";
import { useRouter as usePagesRouter } from "next/router";
import styled from "styled-components";
import { mobile } from "components/templates/Breakpoints";
import { Link } from "@components/atoms";
import { Input, SelectInput, MultiSelectInput, TextArea } from "../../atoms/Form";
import { StaticRoutes } from "@routes";
import { useTranslations } from "next-intl";
import { useLocale } from "hooks";
import { ukPhoneNumberRegex, usPhoneNumberRegex, zaPhoneNumberRegex } from "utils/regex";
import * as Yup from "yup";
import { FormHandles } from "@unform/core";
import { getHubspotFieldsFromSearchQuery, getValidationErrors } from "utils/form";
import { useHubspotForm } from "hooks/useHubspotForm";
import { PERSONAL_DOMAIN_EMAILS } from "utils/_constants";

type GetAQuoteFormProperty =
  | "firstName"
  | "lastName"
  | "email"
  | "jobTitle"
  | "phoneNumber"
  | "numberOfEmployees"
  | "howDidYouHearAboutUs"
  | "whichProducts"
  | "companyName"
  | "country"
  | "howCanWeHelp"
  | "isThereAnythingElseYoudLikeToTellUs";

const HubspotPropertiesMap: Record<GetAQuoteFormProperty, string> = {
  firstName: "firstname",
  lastName: "lastname",
  email: "email",
  jobTitle: "jobtitle",
  phoneNumber: "phone",
  numberOfEmployees: "number_of_employees_string",
  howDidYouHearAboutUs: "how_did_you_hear_about_us_",
  isThereAnythingElseYoudLikeToTellUs: "is_there_anything_else_you_d_like_to_tell_us_",
  whichProducts: "which_products_are_you_most_interested_in_",
  howCanWeHelp: "how_can_we_help____usa",
  companyName: "company",
  country: "country",
};

const BUSINESS_SIZE_OPTIONS_UK = [
  "1 - 2",
  "3 - 20",
  "21 - 50",
  "51 - 100",
  "101 - 250",
  "251 - 500",
  "501 - 2500",
  "2500+",
];

const BUSINESS_SIZE_OPTIONS_GLOBAL = ["1 - 99", "100 - 999", "1,000+"];
const SME_BUSINESS_OPTIONS = BUSINESS_SIZE_OPTIONS_UK.slice(1, 4);
const LE_BUSINESS_OPTIONS = BUSINESS_SIZE_OPTIONS_UK.slice(4, 7);

export const GetAQuoteForm = () => {
  const formRef = useRef<FormHandles>(null);
  const [error, setError] = useState("");
  const [isShaking, setIsShaking] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const router = usePagesRouter();
  const t = useTranslations("get-a-quote");
  const { locale } = useLocale();

  const { handleFormSubmit, loading } = useHubspotForm();

  const submitCompanyDetails = async (formState) => {
    if (formState.numberOfEmployees === "Number of employees") {
      formState.numberOfEmployees = "";
    }

    if (locale === "uk") {
      formState = { ...formState, whichProducts: formState["whichProducts"].replaceAll(",", ";") };
    }

    const validatePhoneNumber = (phoneNumber, locale) => {
      if (
        (locale === "uk" && !ukPhoneNumberRegex.test(phoneNumber)) ||
        (locale === "us" && !usPhoneNumberRegex.test(phoneNumber)) ||
        (locale === "za" && !zaPhoneNumberRegex.test(phoneNumber))
      ) {
        return false;
      }
      return true;
    };

    try {
      const schema = Yup.object().shape({
        email: Yup.string()
          .required("Email is required")
          .email()
          .test("is-personal-email", "Please enter your business email address", (value) =>
            isAllowedEmail(value, PERSONAL_DOMAIN_EMAILS),
          ),
        firstName: Yup.string().required("First name is required"),
        howDidYouHearAboutUs: Yup.string().required("This question is required"),
        jobTitle: Yup.string().required("Job title is required"),
        lastName: Yup.string().required("Last name is required"),
        numberOfEmployees: Yup.string().when([], {
          is: () => locale !== "us",
          then: (schema) => schema.required("Number of fields  required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        phoneNumber: Yup.string()
          .required("Phone number is required")
          .test("is-valid-phone-number", "Please enter a valid phone number", (value) =>
            validatePhoneNumber(value, locale),
          ),
        whichProducts: Yup.string().when([], {
          is: () => locale === "uk",
          then: (schema) => schema.required("This question is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        howCanWeHelp: Yup.string().when([], {
          is: () => locale === "us",
          then: (schema) => schema.required("How can we help field required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        isThereAnythingElseYoudLikeToTellUs: Yup.string().when([], {
          is: () => locale === "us",
          then: (schema) =>
            schema.required("Is there anything else you'd like to tell us field required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        companyName: Yup.string().when([], {
          is: () => locale === "us" || locale === "za",
          then: (schema) => schema.required("Company name is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      });

      await schema.validate(formState, {
        abortEarly: false,
      });

      submitFormToHubspot(formState);
    } catch (err) {
      setIsShaking(true);

      setTimeout(() => {
        setIsShaking(false);
      }, 820);

      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);

        if (formRef.current) {
          formRef.current?.setErrors(errors);
        }
      }

      setError("Check the fields");
    }
  };

  const submitFormToHubspot = async (formState) => {
    const fields = Object.entries(formState).map(([fieldName, value]) => {
      return {
        name: HubspotPropertiesMap[fieldName],
        value,
      };
    });

    const hutk = getCookie("hubspotutk");

    // by now, the parent window should have received a message containing a search string
    // if not, we can try 2 fallback methods
    let parentWindowQuery = "";
    let windowQuery = "";

    // using a try/catch block in case of CORS error
    try {
      parentWindowQuery = window?.parent?.location?.search;
      windowQuery = window && window.location && window.location.search;
    } catch (e) {
      Bugsnag.notify(e);
    }

    const query = parseQuery(searchQuery || parentWindowQuery || windowQuery);

    fields.push(...getHubspotFieldsFromSearchQuery(query));

    const href = window?.location?.href;

    fields.push({
      name: HubspotPropertiesMap.country,
      value: locale || "uk",
    });

    const formData = {
      fields,
      context: {
        hutk,
        pageUri: href || "https://yulife.com/get-a-quote/",
        pageName: "Get a Quote",
      },
    };

    try {
      const form = locale === "us" ? HubspotForms.quoteUS : HubspotForms.quote;
      const results = await handleFormSubmit(form, formData);

      const hasErrors =
        ("status" in results && results.status === "error") ||
        ("errors" in results && results.errors?.length > 0);

      if (hasErrors) {
        return false;
      }

      setTimeout(() => {
        if (typeof window !== "undefined") {
          console.info('"Get a quote" form submitted successfully!');
          window?.fbq?.("trackCustom", "FBGetQuote", {
            email: formState.email,
            env: process.env.ENV_NAME,
          });
          window?.lintrk?.("track", { conversion_id: 10159401 }); // Get-a-quote-event-30-days
          window?.lintrk?.("track", { conversion_id: 12251697 }); // Get-a-quote-event-90-days
        }

        switch (true) {
          case SME_BUSINESS_OPTIONS.includes(formState.numberOfEmployees):
            router.push({
              pathname: StaticRoutes.getAQuoteSuccess,
              query: { variant: "sme" },
            });
            break;
          case LE_BUSINESS_OPTIONS.includes(formState.numberOfEmployees):
            router.push({
              pathname: StaticRoutes.getAQuoteSuccess,
              query: { variant: "le" },
            });
            break;
          default:
            router.push(StaticRoutes.getAQuoteSuccess);
        }
      }); // push to the end of event loop to guarantee any blocking js scripts have loaded

      return true;
    } catch (e) {
      Bugsnag.notify(e);
      return false;
    }
  };

  useEffect(() => {
    const updateSearchQuery = (event: MessageEvent) => {
      // if the parent window sends a message with a search string, update the state
      if (event.data.hasOwnProperty("search")) {
        setSearchQuery(event.data.search);
      }
    };

    window.addEventListener("message", updateSearchQuery);

    return () => window.removeEventListener("message", updateSearchQuery);
  }, []);

  return (
    <FormContainer
      ref={formRef}
      id="get-your-free-quote-form"
      onSubmit={submitCompanyDetails}
      placeholder=""
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
    >
      <StyledP1 bold>{t("form.header")}</StyledP1>
      <Grid2 gap="1.5rem">
        <Input name="firstName" placeholder="First name" type="text" />
        <Input name="lastName" placeholder="Last name" type="text" />
      </Grid2>
      <Input name="email" placeholder="Work email address" type="text" />
      <Input name="jobTitle" placeholder="Job title" type="text" />

      {locale === "us" || locale === "za" ? (
        <Input name="companyName" placeholder="Company name" type="text" />
      ) : null}
      <Input name="phoneNumber" placeholder="Phone number" type="phone" />

      {locale !== "us" ? (
        <SelectInput
          name="numberOfEmployees"
          placeholder="Number of employees"
          options={locale === "uk" ? BUSINESS_SIZE_OPTIONS_UK : BUSINESS_SIZE_OPTIONS_GLOBAL}
        />
      ) : (
        <SelectInput
          name="howCanWeHelp"
          placeholder="How can we help?"
          options={[
            "I am a business and would like a demo or quote",
            "I am an insurance carrier and interested in exploring a partnership",
            "I am an adviser and interested in exploring a partnership",
            "I am already a YuLife customer or have a client with YuLife and would like support",
            "Something else",
          ]}
        />
      )}

      {locale === "uk" ? (
        <MultiSelectInput
          name="whichProducts"
          placeholder="Which products are you most interested in?"
          options={[
            { name: "YuLife employee app", id: "employee_app" },
            { name: "Group Life Insurance", id: "group_life" },
            { name: "Group Health Insurance", id: "business_health_insurance" },
            { name: "Group Income Protection Insurance", id: "group_income_protection" },
            { name: "Group Dental Insurance", id: "group_dental" },
          ]}
          maxLength={1_000}
        />
      ) : null}
      <Input
        name="howDidYouHearAboutUs"
        placeholder="How did you hear about us?"
        type="text"
        maxLength={1_000}
      />
      {locale === "us" ? (
        <TextArea
          name="isThereAnythingElseYoudLikeToTellUs"
          placeholder="Is there anything you'd like to tell us?"
          maxLength={1_000}
        />
      ) : null}
      <QuoteSubmit value="Submit" type="submit" isInputValid={!isShaking} loading={loading} />
      {error && <P1 color="#FA5252">{error}</P1>}
      <P1 color={color.B2100}>
        By submitting your information, you agree to YuLife's{" "}
        <Link passHref href="/privacy-policy/">
          Privacy Policy.
        </Link>
      </P1>
      <P1 color={color.B2100} align="right">
        <Asterisk />
        Required fields
      </P1>
    </FormContainer>
  );
};

const StyledP1 = styled(P1)`
  ${mobile} {
    padding-top: 0;
  }
`;
